import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import Image from "components/elements/image"
import TextLockupV2 from "components/elements/textLockupV2"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { renderInlineMarkdown } from "utils/inlineMarkdown"
import "./section-roadmap-v2.scss"

// A vertical timeline: heading, photo and buttons on the left, steps on a line
// with a colored dot each on the right. The photo is the section's own image,
// or the first step's image when the section has none. A step's label (a year
// such as "2014") shows before its title.

// On phones, a timeline longer than this shows its first few steps and a
// button for the rest.
const LONG_TIMELINE = 5

const SectionRoadmapV2 = ({
  callsToAction,
  image,
  imageCaption,
  roadmapHeading,
  roadmapSteps,
  title,
}) => {
  const [showAll, setShowAll] = useState(false)
  const collapsible = roadmapSteps?.length > LONG_TIMELINE
  const photo = image?.fluid
    ? image
    : roadmapSteps?.find(step => step.image?.fluid)?.image

  return (
    <section className="section-roadmap-v2">
      <div className="container-v2 section-roadmap-v2--layout">
        <div className="section-roadmap-v2--intro">
          {title && (
            <h2 className="section-roadmap-v2--heading">
              <TextLockupV2>{title}</TextLockupV2>
            </h2>
          )}
          {roadmapHeading?.heading && (
            <p className="section-roadmap-v2--heading-text">
              {roadmapHeading.heading}
            </p>
          )}
          {photo?.fluid && (
            <div className="section-roadmap-v2--photo">
              <Image
                image={photo.fluid}
                title={photo.description || photo.title || ""}
              />
              {imageCaption && (
                <p className="section-roadmap-v2--photo-caption">
                  {renderInlineMarkdown(imageCaption)}
                </p>
              )}
            </div>
          )}
          {callsToAction?.length > 0 && (
            <div className="section-roadmap-v2--calls-to-action">
              {callsToAction.map(action => (
                <ButtonV2
                  accessibilityLabel={action.accessibilityLabel}
                  className={getButtonClasses(
                    action.buttonStyle || "primary-green",
                    action.buttonSize || "large"
                  )}
                  href={action.path}
                  key={action.id}
                  text={action.text}
                />
              ))}
            </div>
          )}
        </div>

        {roadmapSteps?.length > 0 && (
          <div>
            <ol
              className={`section-roadmap-v2--steps${
                collapsible && !showAll ? " section-roadmap-v2--collapsed" : ""
              }`}
              id="roadmap-steps"
            >
              {roadmapSteps.map(step => (
                <li className="section-roadmap-v2--step" key={step.id}>
                  <h3 className="section-roadmap-v2--step-title">
                    {step.label && (
                      <span className="section-roadmap-v2--step-label">
                        {step.label}
                      </span>
                    )}
                    {step.title}
                  </h3>
                  {step.text?.childrenMarkdownRemark?.[0]?.html && (
                    <div
                      className="section-roadmap-v2--step-text"
                      dangerouslySetInnerHTML={{
                        __html: step.text.childrenMarkdownRemark[0].html,
                      }}
                    />
                  )}
                </li>
              ))}
            </ol>
            {collapsible && (
              <p className="section-roadmap-v2--toggle">
                <ButtonV2
                  aria-controls="roadmap-steps"
                  aria-expanded={showAll}
                  className={getButtonClasses("Outline Blue", "small")}
                  onClick={() => setShowAll(!showAll)}
                  text={showAll ? "Show less" : "Show the full timeline"}
                />
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export const SectionRoadmapV2Props = {
  callsToAction: PropTypes.array,
  image: PropTypes.object,
  imageCaption: PropTypes.string,
  roadmapHeading: PropTypes.shape({
    heading: PropTypes.string,
  }),
  roadmapSteps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      image: PropTypes.object,
      label: PropTypes.string,
      text: PropTypes.object,
      title: PropTypes.string,
    })
  ),
  title: PropTypes.string,
}

SectionRoadmapV2.propTypes = SectionRoadmapV2Props

export default SectionRoadmapV2
